import { FileSpreadsheet, FolderOpen, Microscope, Tag, Target } from "lucide-react";
import { ComponentType } from "react";
import { Mask } from "@/types/mask";
import { MeasurementSystem } from "@/types/measurementSystem";
import { Project } from "@/types/project";
import { SpecListMinimal } from "@/types/specList";
import { TestLabel } from "@/types/testLabel";

export type HoverDetail = { label: string; value: string | string[] | null };

export type SearchResult = {
  id: string;
  label: string;
  extraInfo?: string;
  typeLabel: string;
  icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  route: string | null;
  group: string;
  hoverDetails: HoverDetail[];
};

export type GroupedResults = Record<string, { item: SearchResult; globalIndex: number }[]>;

/**
 * Filters and maps projects to search results.
 * Matches against name, description, and owner.
 */
export function applyProjectFilters(
  projects: Project[],
  q: string,
  maxPerGroup: number,
): SearchResult[] {
  return projects
    .filter(
      (p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q) ||
        p.owner.toLowerCase().includes(q),
    )
    .slice(0, maxPerGroup)
    .map((p) => ({
      id: `project-${p.id}`,
      label: p.name,
      icon: FolderOpen,
      typeLabel: "Project",
      iconClassName: "text-green-500",
      route: `/projects/${p.id}`,
      group: "Projects",
      hoverDetails: [
        { label: "Owner", value: p.owner },
        { label: "Description", value: p.description ?? null },
        { label: "Start", value: p.startDate?.toLocaleDateString() ?? null },
        { label: "End", value: p.endDate?.toLocaleDateString() ?? null },
        { label: "Funding", value: p.funding ?? null },
      ],
    }));
}

/**
 * Filters and maps masks to search results.
 * Matches against name and PCM version. Masks assigned to a project are sorted first;
 * unassigned masks have no route and cannot be navigated to.
 *
 * @param projectNameById - Used to populate `extraInfo` with the parent project name.
 */
export function applyMaskFilters(
  masks: Mask[],
  q: string,
  maxPerGroup: number,
  projectNameById: Map<number, string>,
): SearchResult[] {
  return masks
    .filter(
      (m) =>
        !q || m.name.toLowerCase().includes(q) || m.pcmVersion.pcmVersion.toLowerCase().includes(q),
    )
    .sort((a, b) => (a.projectId != null ? -1 : 1) - (b.projectId != null ? -1 : 1))
    .slice(0, maxPerGroup)
    .map((m) => ({
      id: `mask-${m.id}`,
      label: m.name,
      extraInfo: m.projectId != null ? (projectNameById.get(m.projectId) ?? undefined) : undefined,
      icon: Target,
      typeLabel: "Mask",
      iconClassName: "text-purple-500",
      route: m.projectId != null ? `/projects/${m.projectId}/${m.id}` : null,
      group: "Masks",
      hoverDetails: [
        { label: "PCM Version", value: m.pcmVersion.pcmVersion },
        { label: "Wafer Ø", value: m.waferDiameter },
        {
          label: "Die",
          value: m.dieWidth && m.dieHeight ? `${m.dieWidth} × ${m.dieHeight} µm` : null,
        },
      ],
    }));
}

/**
 * Filters and maps spec lists to search results.
 * Matches against name and associated mask name.
 */
export function applySpecListFilters(
  specLists: SpecListMinimal[],
  q: string,
  maxPerGroup: number,
): SearchResult[] {
  return specLists
    .filter(
      (s) =>
        !q || s.name.toLowerCase().includes(q) || (s.mask?.name ?? "").toLowerCase().includes(q),
    )
    .slice(0, maxPerGroup)
    .map((s) => ({
      id: `spec-${s.id}`,
      label: s.name,
      extraInfo: s.mask?.name,
      icon: FileSpreadsheet,
      typeLabel: "Spec List",
      iconClassName: "text-yellow-500",
      route: `/spec-lists/${s.id}/version/${s.latestVersion.id}`,
      group: "Specs",
      hoverDetails: [
        { label: "Type", value: s.type },
        { label: "Mask", value: s.mask?.name ?? null },
        { label: "Version", value: `v${s.latestVersion.version}` },
        { label: "Created by", value: s.latestVersion.createdBy },
      ],
    }));
}

/**
 * Filters and maps measurement systems to search results.
 * Matches against name and location.
 */
export function applySystemFilters(
  systems: MeasurementSystem[],
  q: string,
  maxPerGroup: number,
): SearchResult[] {
  return systems
    .filter((s) => !q || s.name.toLowerCase().includes(q) || s.location.toLowerCase().includes(q))
    .slice(0, maxPerGroup)
    .map((s) => ({
      id: `system-${s.id}`,
      label: s.name,
      extraInfo: s.location,
      icon: Microscope,
      typeLabel: "System",
      iconClassName: "text-blue-500",
      route: `/systems/${s.id}`,
      group: "Systems",
      hoverDetails: [
        { label: "Location", value: s.location },
        { label: "Config", value: s.configuration },
        { label: "Automation", value: s.automation_level },
        { label: "Wafer size", value: s.wafer_size },
        {
          label: "Capabilities",
          value: s.capabilities.length > 0 ? s.capabilities.map((c) => c.name) : null,
        },
      ],
    }));
}

/**
 * Filters and maps test labels to search results.
 * Matches against name, description, and associated mask name.
 */
export function applyTestLabelFilters(
  testLabels: TestLabel[],
  q: string,
  maxPerGroup: number,
): SearchResult[] {
  return testLabels
    .filter((tl) => !tl.isArchived)
    .filter(
      (tl) =>
        !q ||
        tl.name.toLowerCase().includes(q) ||
        (tl.description ?? "").toLowerCase().includes(q) ||
        tl.mask.name.toLowerCase().includes(q),
    )
    .slice(0, maxPerGroup)
    .map((tl) => ({
      id: `test-label-${tl.id}`,
      label: tl.name,
      extraInfo: tl.mask.name,
      icon: Tag,
      typeLabel: "Test Label",
      iconClassName: "text-emerald-500",
      route: `/test-labels/${tl.id}`,
      group: "Test Labels",
      hoverDetails: [
        { label: "Mask", value: tl.mask.name },
        { label: "Status", value: tl.status === "VALIDATED" ? "Validated" : "Not Validated" },
        { label: "Description", value: tl.description ?? null },
        { label: "Capability", value: tl.measurementCapability?.name ?? null },
      ],
    }));
}

/**
 * Groups a flat list of search results by their `group` field, preserving each item's
 * original flat-list index as `globalIndex`. This index is used by consumers that need
 * a single monotonically increasing cursor across all groups (e.g. keyboard navigation).
 *
 * @example
 * // Given results = [projectA, maskA, maskB]
 * // Returns { Projects: [{ item: projectA, globalIndex: 0 }],
 * //           Masks:    [{ item: maskA,    globalIndex: 1 },
 * //                      { item: maskB,    globalIndex: 2 }] }
 */
export function groupResultsByCategory(results: SearchResult[]): GroupedResults {
  const map: GroupedResults = {};
  results.forEach((item, idx) => {
    (map[item.group] ??= []).push({ item, globalIndex: idx });
  });
  return map;
}
